import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchExtractionDetails, fetchTier1Slugs } from '@/utils/externalDiffsAPI';

function StatusBadge({ status }: { status: string }) {
  if (status === 'success' || status === 'partial') {
    return (
      <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">
        Extracted
      </span>
    );
  }
  if (status === 'fallback') {
    return (
      <span className="inline-flex items-center text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
        Fallback
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded border border-red-200">
        Failed
      </span>
    );
  }
  return <span className="text-xs text-gray-400">{status}</span>;
}

export default function DashboardExtractions() {
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<'all' | 'tier1' | 'tier2'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'extracted' | 'fallback' | 'failed'>('all');

  const { data: details, isLoading, error } = useQuery({
    queryKey: ['dashboard-extraction-details'],
    queryFn: fetchExtractionDetails,
    staleTime: 2 * 60 * 1000,
  });

  const { data: tier1Slugs } = useQuery({
    queryKey: ['dashboard-tier1-slugs'],
    queryFn: fetchTier1Slugs,
    staleTime: 10 * 60 * 1000,
  });

  const tier1Set = useMemo(() => new Set(tier1Slugs ?? []), [tier1Slugs]);

  const counts = useMemo(() => {
    const all = details ?? [];
    return all.reduce((acc, d) => {
      if (d.status === 'success' || d.status === 'partial') acc.extracted++;
      else if (d.status === 'fallback') acc.fallback++;
      else if (d.status === 'failed') acc.failed++;
      return acc;
    }, { extracted: 0, fallback: 0, failed: 0 });
  }, [details]);

  const rows = useMemo(() => {
    const all = details ?? [];
    return all
      .filter(d => {
        if (tierFilter === 'tier1') return tier1Set.has(d.slug);
        if (tierFilter === 'tier2') return !tier1Set.has(d.slug);
        return true;
      })
      .filter(d => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'extracted') return d.status === 'success' || d.status === 'partial';
        if (statusFilter === 'fallback') return d.status === 'fallback';
        if (statusFilter === 'failed') return d.status === 'failed';
        return true;
      })
      .filter(d => !search || d.slug.toLowerCase().includes(search.toLowerCase()));
  }, [details, tier1Set, tierFilter, statusFilter, search]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/dashboard" className="text-sm text-gray-400 hover:text-gray-600">&larr; Dashboard</Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-lg font-semibold text-gray-900">Extraction Details</h1>
          {!isLoading && details && (
            <span className="ml-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
              {details.length.toLocaleString()} total
            </span>
          )}
        </div>

        {/* Status filter chips */}
        {!isLoading && details && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button onClick={() => setStatusFilter('all')} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === 'all' ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
              All {details.length}
            </button>
            <button onClick={() => setStatusFilter('extracted')} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === 'extracted' ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 text-green-700 border-green-200 hover:border-green-400'}`}>
              Extracted {counts.extracted}
            </button>
            <button onClick={() => setStatusFilter('fallback')} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === 'fallback' ? 'bg-amber-500 text-white border-amber-500' : 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400'}`}>
              Fallback {counts.fallback}
            </button>
            <button onClick={() => setStatusFilter('failed')} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === 'failed' ? 'bg-red-500 text-white border-red-500' : 'bg-red-50 text-red-700 border-red-200 hover:border-red-400'}`}>
              Failed {counts.failed}
            </button>
          </div>
        )}

        {/* Tier filter + search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            {(['all', 'tier1', 'tier2'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTierFilter(t)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  tierFilter === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'all' ? 'All' : t === 'tier1' ? 'Tier 1' : 'Tier 2'}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search slugs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="text-sm border border-gray-200 rounded px-3 py-1.5 bg-white w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {rows.length > 0 && (
            <span className="text-xs text-gray-400">{rows.length.toLocaleString()} shown</span>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 space-y-3">
              {[...Array(8)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
            </div>
          ) : error ? (
            <p className="p-8 text-sm text-red-500">Failed to load: {(error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-sm text-gray-400 italic">
              {search || tierFilter !== 'all' || statusFilter !== 'all' ? 'No slugs match your filters.' : 'No extractions recorded yet.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-12">#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Slug</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Tier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Method</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Version</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Plans</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Features</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Extracted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((row, i) => {
                  const isTier1 = tier1Set.has(row.slug);
                  return (
                    <tr key={row.slug} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5 text-gray-400 tabular-nums">{i + 1}</td>
                      <td className="px-4 py-2.5 font-mono text-gray-800">{row.slug}</td>
                      <td className="px-4 py-2.5">
                        {tier1Slugs ? (
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${isTier1 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isTier1 ? 'T1' : 'T2'}
                          </span>
                        ) : (
                          <span className="text-gray-300">&mdash;</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">
                        {row.status === 'fallback' && row.fallback_method ? (
                          <span title={row.fallback_reason || ''}>
                            <span className="text-gray-400">{row.method.replace(/_/g, ' ')}</span>
                            {' → '}
                            <span className="font-medium">{row.fallback_method.replace(/_/g, ' ')}</span>
                          </span>
                        ) : (
                          <span>{row.method.replace(/_/g, ' ')}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs font-mono text-gray-500">{row.version || '—'}</td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-700">{row.plan_count}</td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-700">{row.feature_count}</td>
                      <td className="px-4 py-2.5 text-xs tabular-nums text-gray-500">
                        {row.duration_seconds != null ? `${Math.floor(row.duration_seconds / 60)}m ${row.duration_seconds % 60}s` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-400">
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
